import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './VendorDashboard.css';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const vendorToken = localStorage.getItem('vendorToken');
    const vendorInfo = localStorage.getItem('vendorInfo');

    if (!vendorToken || !vendorInfo) {
      navigate('/vendor/login');
      return;
    }

    setVendor(JSON.parse(vendorInfo));
    loadDashboardData(vendorToken);
  }, [navigate]);

  const loadDashboardData = async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/v1/chat/conversations`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vendorToken');
    localStorage.removeItem('vendorInfo');
    localStorage.removeItem('userType');
    navigate('/vendor/login');
  };

  const openChat = (conversationId) => {
    navigate(`/vendor/chat/${conversationId}`);
  };

  if (loading) {
    return (
      <div className="vendor-dashboard-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="vendor-dashboard">
      {/* Header */}
      <header className="vendor-dashboard-header">
        <div className="header-left">
          <h1>Vendor Portal</h1>
          <span className="vendor-code">{vendor?.vendor_code}</span>
        </div>
        <div className="header-right">
          <div className="vendor-info">
            <span className="vendor-name">{vendor?.company_name}</span>
            <span className="vendor-status">{vendor?.status}</span>
          </div>
          <button onClick={handleLogout} className="logout-button">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="vendor-dashboard-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-home"></i>
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <i className="fas fa-comments"></i>
          Messages
          {conversations.length > 0 && (
            <span className="badge">{conversations.length}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <i className="fas fa-user"></i>
          Profile
        </button>
      </div>

      {/* Content */}
      <div className="vendor-dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="welcome-card">
              <h2>Welcome back, {vendor?.company_name}!</h2>
              <p>Manage your communications and stay connected with your organization.</p>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <i className="fas fa-comments"></i>
                <div className="stat-info">
                  <h3>{conversations.length}</h3>
                  <p>Active Conversations</p>
                </div>
              </div>
              <div className="stat-card">
                <i className="fas fa-check-circle"></i>
                <div className="stat-info">
                  <h3>{vendor?.status}</h3>
                  <p>Account Status</p>
                </div>
              </div>
              <div className="stat-card">
                <i className="fas fa-building"></i>
                <div className="stat-info">
                  <h3>{vendor?.organization?.name || 'N/A'}</h3>
                  <p>Organization</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="chat-section">
            <div className="chat-header">
              <h2>Your Conversations</h2>
              <p>Click on a conversation to open the chat</p>
            </div>

            {conversations.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-comments"></i>
                <h3>No conversations yet</h3>
                <p>Your conversations will appear here once they are created.</p>
              </div>
            ) : (
              <div className="conversations-list">
                {conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className="conversation-card"
                    onClick={() => openChat(conversation.id)}
                  >
                    <div className="conversation-icon">
                      <i className="fas fa-comments"></i>
                    </div>
                    <div className="conversation-info">
                      <h3>{conversation.title || 'Untitled Conversation'}</h3>
                      <p className="conversation-type">{conversation.type}</p>
                      {conversation.messages && conversation.messages[0] && (
                        <p className="last-message">
                          {conversation.messages[0].content}
                        </p>
                      )}
                    </div>
                    <div className="conversation-meta">
                      <span className="conversation-date">
                        {new Date(conversation.last_message_at).toLocaleDateString()}
                      </span>
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="profile-card">
              <h2>Company Profile</h2>
              <div className="profile-details">
                <div className="profile-field">
                  <label>Company Name</label>
                  <p>{vendor?.company_name}</p>
                </div>
                <div className="profile-field">
                  <label>Email</label>
                  <p>{vendor?.email}</p>
                </div>
                <div className="profile-field">
                  <label>Vendor Code</label>
                  <p>{vendor?.vendor_code}</p>
                </div>
                <div className="profile-field">
                  <label>Status</label>
                  <p className={`status-badge ${vendor?.status?.toLowerCase()}`}>
                    {vendor?.status}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDashboard;


